import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { X, FileText, File, Download, Eye, ChevronLeft, ZoomIn, ZoomOut } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DocumentsModal = ({ isOpen, onClose }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewingDoc, setViewingDoc] = useState(null);
  const [viewContent, setViewContent] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    if (isOpen) {
      fetchDocuments();
    }
  }, [isOpen]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/platform-documents?enabled_only=true`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setDocuments(response.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFileIcon = (fileType) => {
    switch (fileType) {
      case 'pdf':
        return <File className="w-8 h-8 text-red-500" />;
      case 'image':
        return <File className="w-8 h-8 text-blue-500" />;
      case 'word':
        return <FileText className="w-8 h-8 text-blue-700" />;
      case 'excel':
        return <FileText className="w-8 h-8 text-green-600" />;
      case 'text':
        return <FileText className="w-8 h-8 text-gray-500" />;
      default:
        return <File className="w-8 h-8 text-gray-400" />;
    }
  };

  const handleView = async (doc) => {
    try {
      setViewLoading(true);
      setViewingDoc(doc);
      setZoom(1);
      
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/platform-documents/${doc.id}/view`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        responseType: 'blob',
        timeout: 30000
      });

      if (!response.data || response.data.size === 0) {
        throw new Error('Empty response');
      }

      const contentType = response.headers['content-type'] || 'application/octet-stream';
      const blob = new Blob([response.data], { type: contentType });
      const blobUrl = URL.createObjectURL(blob);
      
      setViewContent({
        url: blobUrl,
        type: contentType,
        isImage: contentType.startsWith('image/'),
        isPdf: contentType === 'application/pdf'
      });
    } catch (error) {
      console.error('Error viewing document:', error);
      alert('Ошибка при открытии документа');
      setViewingDoc(null);
    } finally {
      setViewLoading(false);
    }
  };

  const closeViewer = () => {
    if (viewContent?.url) {
      URL.revokeObjectURL(viewContent.url);
    }
    setViewContent(null);
    setViewingDoc(null);
    setZoom(1);
  };

  const handleDownload = async (doc) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/platform-documents/${doc.id}/view`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        responseType: 'blob'
      });

      const blob = new Blob([response.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Ошибка при скачивании документа');
    }
  };

  if (!isOpen) return null;

  // Document viewer mode
  if (viewingDoc && viewContent) {
    return ReactDOM.createPortal(
      <>
        <div className="fixed inset-0 bg-black" style={{zIndex: 2147483646}} />
        <div className="fixed inset-0 bg-black/95 flex flex-col" style={{zIndex: 2147483647}}>
        {/* Header */}
        <div className="flex items-center justify-between p-3 bg-gray-900 border-b border-gray-700">
          <button
            onClick={closeViewer}
            className="flex items-center space-x-2 text-white hover:text-blue-400 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            <span>Назад</span>
          </button>
          
          <h3 className="text-white font-medium truncate max-w-[50%]">
            {viewingDoc.title}
          </h3>
          
          <div className="flex items-center space-x-2">
            {viewContent.isImage && (
              <>
                <button
                  onClick={() => setZoom(z => Math.max(0.5, z - 0.25))}
                  className="p-2 text-white hover:bg-gray-700 rounded-lg"
                  title="Уменьшить"
                >
                  <ZoomOut className="w-5 h-5" />
                </button>
                <span className="text-white text-sm">{Math.round(zoom * 100)}%</span>
                <button
                  onClick={() => setZoom(z => Math.min(3, z + 0.25))}
                  className="p-2 text-white hover:bg-gray-700 rounded-lg"
                  title="Увеличить"
                >
                  <ZoomIn className="w-5 h-5" />
                </button>
              </>
            )}
            <button
              onClick={() => handleDownload(viewingDoc)}
              className="p-2 text-white hover:bg-gray-700 rounded-lg"
              title="Скачать"
            >
              <Download className="w-5 h-5" />
            </button>
            <button
              onClick={closeViewer}
              className="p-2 text-white hover:bg-gray-700 rounded-lg"
              title="Закрыть"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-auto flex items-center justify-center p-4">
          {viewContent.isImage ? (
            <img
              src={viewContent.url}
              alt={viewingDoc.title}
              className="max-w-full max-h-full object-contain transition-transform duration-200"
              style={{ transform: `scale(${zoom})` }}
            />
          ) : viewContent.isPdf ? (
            <iframe
              src={viewContent.url}
              className="w-full h-full bg-white rounded-lg"
              title={viewingDoc.title}
            />
          ) : (
            <div className="text-center text-gray-400">
              <File className="w-16 h-16 mx-auto mb-4" />
              <p>Предпросмотр недоступен для этого типа файла</p>
              <button
                onClick={() => handleDownload(viewingDoc)}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Скачать файл
              </button>
            </div>
          )}
        </div>
      </div>
      </>,
      document.body
    );
  }

  // Documents list mode
  return ReactDOM.createPortal(
    <>
      <div className="fixed inset-0 bg-black" style={{zIndex: 2147483646}} />
      <div className="fixed inset-0 bg-black/90 flex items-center justify-center p-4" style={{zIndex: 2147483647}}>
      <div className="bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col border border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <h2 className="text-lg font-semibold text-white flex items-center">
            <FileText className="w-5 h-5 mr-2 text-purple-400" />
            Документы
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
            </div>
          ) : documents.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Документы не найдены</p>
            </div>
          ) : (
            <div className="space-y-3">
              {documents.map((doc) => (
                <div
                  key={doc.id}
                  className="bg-gray-700/50 rounded-lg p-3 hover:bg-gray-700 transition-colors"
                >
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 p-2 bg-gray-600 rounded-lg">
                      {getFileIcon(doc.file_type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white font-medium truncate">{doc.title}</h3>
                      {doc.description && (
                        <p className="text-gray-400 text-sm mt-1 line-clamp-2">{doc.description}</p>
                      )}
                      <p className="text-gray-500 text-xs mt-1">
                        {(doc.file_size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  </div>
                  <div className="flex space-x-2 mt-3">
                    <button
                      onClick={() => handleView(doc)}
                      disabled={viewLoading}
                      className="flex-1 flex items-center justify-center space-x-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm disabled:opacity-50"
                    >
                      <Eye className="w-4 h-4" />
                      <span>Открыть</span>
                    </button>
                    <button
                      onClick={() => handleDownload(doc)}
                      className="flex items-center justify-center px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500 transition-colors text-sm"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
    </>,
    document.body
  );
};

export default DocumentsModal;
